import React from "react";
import { Link, NavLink } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";

import fsciLogo from "src/assets/images/fsci_report_logo.png";
import ffiLogo from "src/assets/images/ffi_report_logo.svg";
import "./report_tabs.scss";
import { PrintIcon } from "../../assets/icons/print_icon";
import { School, Viewer } from "src/generated/types";
import { FSCIBrandedV2Report } from "../../pages/reports/fsci/v2/types";
import { FSCIBrandedV1Report } from "../../pages/reports/fsci/v1/types";
import { FFIBrandedV1Report } from "../../pages/reports/ffi/v1/types";

export const GET_REPORT_QUERY = gql`
    query GetReport($searchType: String!, $searchYear: String!) {
        viewer {
            user {
                id
                school {
                    id
                    reports(
                        where: {
                            results: { exists: true }
                            type: { equalTo: $searchType }
                            year: { equalTo: $searchYear }
                        }
                    ) {
                        count
                        edges {
                            node {
                                id
                                year
                                type
                            }
                        }
                    }
                }
            }
        }
    }
`;

interface ReportTabsProps {
    currentReport: FSCIBrandedV2Report | FSCIBrandedV1Report | FFIBrandedV1Report;
    readonly school: School;
}

interface ApolloQueryData {
    readonly viewer: Viewer;
}

interface QueryVariables {
    readonly searchYear: string;
    readonly searchType: string;
}

const reportTypes = ["fsci", "ffi"];
const fsciName = "ACSI Flourishing School Culture Instrument";
const ffiName = "Flourishing Faith Index";

export const ReportTabs = ({ currentReport, school }: ReportTabsProps): React.ReactElement => {
    // Set "searchType" to the opposite type of the current report (FSCI or FFI)
    // This will be used to check if the opposite report exists for the same year
    // If it doesn't, then permanently disable the corresponding tab in the UI
    const searchType = currentReport.value.type === "fsci" ? "ffi" : "fsci";
    const searchYear = currentReport.value.year;

    const { data, error } = useQuery<ApolloQueryData, QueryVariables>(GET_REPORT_QUERY, {
        variables: { searchType, searchYear },
    });

    if (error) {
        console.error(error);
    }

    return (
        <div className="report-page-title-line">
            <div className="report-page-meta">
                <h2 className="report-page-subtitle">
                    {currentReport.value.type === "fsci"
                        ? "ACSI Flourishing School Culture Instrument"
                        : "Flourishing Faith Index"}
                </h2>
                <h3 className="report-page-year">{currentReport.value.year}</h3>
                <Link
                    className="report-print"
                    to={{
                        pathname: `/reports/${currentReport.value.type}/${currentReport.value.year}/print`,
                        state: { school, currentReport },
                    }}
                >
                    <PrintIcon /> Print/Download
                </Link>
            </div>
            <div className="report-tabs">
                {reportTypes.map((reportType, index) => {
                    let report = null;
                    // If the current report is the reportType, set it to 'report'
                    if (currentReport.value.type === reportType) {
                        report = currentReport.value;

                        // If the other report type exists for the year and it equals the reportType, set it to 'report'
                    } else if (
                        data?.viewer.user.school?.reports.edges &&
                        data?.viewer.user.school?.reports.edges[0]?.node?.type === reportType
                    ) {
                        report = data?.viewer.user.school?.reports.edges[0]?.node;
                    }
                    // In all other cases, report will be falsy (null)

                    if (report) {
                        return (
                            <NavLink
                                activeClassName="active-link"
                                className={`report-tab ${currentReport.value.type === reportType ? "active-link" : ""}`}
                                key={index}
                                to={`/reports/${report.type}/${report.year}/overview`}
                            >
                                <img
                                    alt={report.type === "fsci" ? fsciName : ffiName}
                                    src={report.type === "fsci" ? fsciLogo : ffiLogo}
                                />
                            </NavLink>
                        );
                    } else {
                        return (
                            <div className="report-tab" key={index}>
                                <img
                                    alt={reportType === "fsci" ? fsciName : ffiName}
                                    src={reportType === "fsci" ? fsciLogo : ffiLogo}
                                />
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    );
};
